import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { propertyNames, tableNames } from '../../app/app.constants';
import { dynamicSort, GlobalVars } from '../../app/app.environment';
import { QueryModel } from '../../models/query';
import { Role } from '../../models/roles';
import { User } from '../../models/user';
import { UserCreds } from '../../models/user-creds';
import { AreaProvider } from '../area/area';
import { AuthProvider } from '../auth/auth';
import { FirebaseProvider } from '../firebase/firebase';


@Injectable()
export class UserProvider {

  constructor(
    private areaProvider: AreaProvider,
    public authProvider: AuthProvider,
    public firebaseService: FirebaseProvider,
    public globalVars: GlobalVars,
  ) {}

  getMicRRHHInfo(userId?: string): Observable<User> {
    return this.firebaseService.object(this.getBaseRoot() + (userId || this.globalVars.user.key) + '/' + tableNames.MicRRHH);
  }

  /**
   * Recuperar nom d'un usuari segons ID.
   * 
   * @param userId ID de l'usuari.
   */
  getNameOnce(userId: string): Observable<any> {
    return this.firebaseService.propertyOnce(this.getBaseRoot() + userId + '/' + propertyNames.Name)
  }

  getPetitions(userId?: string): Observable<any> {
    return this.firebaseService.list(this.getBaseRoot() + (userId || this.globalVars.user.key) + '/' + tableNames.Petitions);
  }

  /**
   * Recuperar informació de l'usuari segons ID.
   * 
   * @param userId ID de l'usuari. Per defecte es fa servir la ID de l'usuari actual.
   */
  getUser(userId?: string): Observable<User> {
    return this.firebaseService.object(this.getBaseRoot() + (userId || this.globalVars.user.key));
  }

  /**
   * Recuperar informació de l'usuari segons ID.
   * 
   * @param userId ID de l'usuari. Per defecte es fa servir la ID de l'usuari actual.
   */
  getUserOnce(userId?: string): Observable<User> {
    return this.firebaseService.objectOnce(this.getBaseRoot() + (userId || this.globalVars.user.key));
  }

  /**
   * Recuperar informació de l'usuari segons ID.
   * 
   * @param userId ID de l'usuari. Per defecte es fa servir la ID de l'usuari actual.
   */
  getUserData(userId?: string): Observable<User> {
    return this.firebaseService.object(this.getBaseRoot() + (userId || this.globalVars.user.key));
  }

  /**
   * Recuperar usuaris.
   */
  getUsers(): Observable<User[]> {
    return this.firebaseService.list(this.getBaseRoot()).map(users => users.sort(dynamicSort('name')));
  }

  /**
   * Recuperar usuaris (només una vegada).
   */
  getUsersOnce(): Observable<User[]> {
    return this.firebaseService.listOnce(this.getBaseRoot());
  }

  getUsersByHeadArea(userId: string, includeSelfUser: boolean): Promise<Observable<User[]>> {

    return this.areaProvider.getAllAreasOnce().toPromise().then(areas => {
      var involvedAreas = [];

      if (this.globalVars.user.role == Role.admin) {
        involvedAreas = areas.map(area => area.key)
      }

      else {
        areas.forEach(area => {
          if (area.headsIds && area.headsIds.map(head => head.key).indexOf(userId) >= 0) involvedAreas.push(area.key);
        })
      }

      console.log(involvedAreas)

      if (involvedAreas.length == 0) return this.getUser(userId).map(user => [user])
      
      else return this.getUsers().map(users =>
        users.filter(user => (includeSelfUser && user.key == this.globalVars.user.key)
          || involvedAreas.indexOf(user.areaId) >= 0
        )
      )
    })
  }

  /**
   * Recuperar usuaris segons un criteri de cerca.
   * 
   * @param criteria Criteri de cerca.
   * @param field Camp utilitzat per filtrar.
   */
  getUsersBySearch(criteria?: string, field?: string): Observable<User[]> {
    return this.getUsers().map(users => users.filter(user => {
      return (user[field].toLowerCase().indexOf(criteria.toLowerCase()) > -1);
    }))
  }

  /**
   * Recuperar noms dels usuaris del repositori indicat.
   * 
   * @param repo Repositori. Per defecte es fa servir el repositori de l'usuari actual.
   */
  getUserNames(repo?: string): Observable<any[]> {
    let query = new QueryModel();

    query.orderByChild = 'repo';
    query.equalTo = repo ? repo : this.globalVars.user.repo;

    return this.firebaseService.list(tableNames.User, query)
      .map(users => users.map(user => { return {key: user.key, name: user.name} }).sort(dynamicSort('name')));
  }

  /**
   * Recuperar noms dels usuaris del repositori indicat segons un criteri de cerca.
   * 
   * @param repo Repositori. Per defecte es fa servir el repositori de l'usuari actual.
   * @param criteria Criteri de cerca.
   * @param field Camp utilitzat per filtrar.
  */
  getUserNamesBySearch(repo?: string, criteria?: string, field?: string): Observable<any[]> {
    return this.getUserNames().map(users => users.filter(user => {
      return (user[field].toLowerCase().indexOf(criteria.toLowerCase()) > -1);
    }))
  }

  /**
   * Recuperar dades públiques dels usuaris del repositori indicat.
   * 
   * @param repo Repositori. Per defecte es fa servir el repositori de l'usuari actual.
   */
  getUsersPublicData(repo?: string): Observable<User[]> {
    let query = new QueryModel();

    query.orderByChild = 'repo';
    query.equalTo = repo ? repo : this.globalVars.user.repo;

    return this.firebaseService.listOnce(tableNames.User, query);
  }

  /**
   * Recuperar dades públiques dels usuaris del repositori indicat.
   * 
   * @param repo Repositori. Per defecte es fa servir el repositori de l'usuari actual.
   */
  getUsersPublicDataOnce(repo?: string): Observable<User[]> {
    let query = new QueryModel();

    query.orderByChild = 'repo';
    query.equalTo = repo ? repo : this.globalVars.user.repo;

    return this.firebaseService.listOnce(tableNames.User, query);
  }

  /**
   * Registrar un usuari al sistema.
   * 
   * @param user Usuari a registrar.
   */
  register(user: User): Promise<any> {
    return new Promise((resolve, reject) => {
      this.authProvider.signUp({ email: user.email, password: user.password } as UserCreds).then(userUid => {

        delete user.password;
        user.role = Number(user.role);
        user.repo = this.globalVars.user.repo;

        // Preparar dades públiques de l'usuari
        let userPublicData = Object.assign({}, user);
        delete userPublicData.state;
        //delete userPublicData.timetables;

        // Preparar inserció simultània
        let mainPath = tableNames.User
        
        var updates = {};
        updates[mainPath + '/' + userUid] = userPublicData;
        if (this.globalVars.user.repo != '') updates[this.getBaseRoot() + '/' + userUid] = user;
        
        this.firebaseService.directUpdate(updates).then(() => {
          resolve(true)
        }).catch(() => {
          reject('Error al registrar usuario.')
        })
      }).catch(err => {
        console.log(err)
        reject('Error al registrar usuario.')
      })
    })
  }

  /**
   * Guardar usuari al sistema.
   * 
   * @param user Usuari a guardar.
   */
  save(user: User): Promise<any> {
    if (user.key) {
      var key = user.key;
      delete user.key;
  
      // Preparar dades públiques de l'usuari
      let userPublicData = Object.assign({}, user);

      // Preparar inserció simultània
      var updates = {};
      updates[tableNames.User + '/' + key] = userPublicData;
      
      if (this.globalVars.user.repo != '') updates[this.getBaseRoot() + '/' + key] = user;
      
      return this.firebaseService.directUpdate(updates);
    }
      
    else return this.register(user);
  }

  /**
   * Arrel base dins la base de dades.
   */
  private getBaseRoot(): string {
    return tableNames.Repo + '/' + this.globalVars.user.repo + '/' + tableNames.User + '/';
  }

  addLabels() {

    this.getUsersOnce().subscribe(users => {

      console.log(users)

      users.map((user: any) => {

        // user.extraDay = {}
        // user.extraDay[2020] = false;

        if (user.pendingHolidays == null) user.pendingHolidays = {}
        if (user.pendingAPs == null) user.pendingAPs = {}

        user.pendingHolidays[2019] = 0;
        user.pendingAPs[2019] = 0;

        this.save(user)
      })
    })
  }
}


// WEBPACK FOOTER //
// ./src/providers/user/user.ts